import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  RefreshCw,
  LogOut,
  Users,
  BarChart3,
  Image as ImageIcon,
  Newspaper,
  Languages,
  Radio,
  Activity,
  PlayCircle,
  ShieldCheck,
  Send,
  Handshake,
  Lock,
  Globe,
  GitMerge,
  History,
  CalendarCheck,
} from "lucide-react";
import { DigestAnalyticsTab } from "./DigestAnalyticsTab";
import { EssentialsTab } from "./EssentialsTab";
import { SelfServeAdsTab } from "./SelfServeAdsTab";
import { OverviewTab } from "./admin/OverviewTab";
import { ConfigHealthBanner } from "./admin/ConfigHealthBanner";
import { ScraperHealthBanner } from "./admin/ScraperHealthBanner";
import { ScraperHealthCards } from "./admin/ScraperHealthCards";
import { ArticlesTab } from "./admin/ArticlesTab";
import { HealthTab } from "./admin/HealthTab";
import { SourcesTab } from "./admin/SourcesTab";
import { AdsTab } from "./admin/AdsTab";
import { SubscribersTab } from "./admin/SubscribersTab";
import { IslandsTab } from "./admin/IslandsTab";
import { DigestTab } from "./admin/DigestTab";
import { SponsorsTab } from "./admin/SponsorsTab";
import { ComplianceTab } from "./admin/ComplianceTab";
import { DedupReviewTab } from "./admin/DedupReviewTab";
import { HistoryReviewTab } from "./admin/HistoryReviewTab";
import { MarqueeFreshnessTab } from "./admin/MarqueeFreshnessTab";
import { SponsorSlotsTab } from "./admin/SponsorSlotsTab";

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "articles", label: "Articles", icon: Newspaper },
  { id: "islands", label: "Islands", icon: Globe },
  { id: "health", label: "Scraper Health", icon: Activity },
  { id: "sources", label: "Sources", icon: Radio },
  { id: "sponsor_slots", label: "Sponsor Slots", icon: Lock },
  { id: "ads", label: "Ad Slots", icon: ImageIcon },
  { id: "self_serve_ads", label: "Self-Serve Ads", icon: ImageIcon },
  { id: "essentials", label: "Island Essentials", icon: PlayCircle },
  { id: "subscribers", label: "Subscribers", icon: Users },
  { id: "digest_analytics", label: "Digest Analytics", icon: BarChart3 },
  { id: "digest", label: "WhatsApp Digest", icon: Send },
  { id: "sponsors", label: "Sponsor Inquiries", icon: Handshake },
  { id: "compliance", label: "Compliance & Takedown", icon: ShieldCheck },
  { id: "dedup", label: "Duplicate Review", icon: GitMerge },
  { id: "history", label: "Historical Photos", icon: History },
  { id: "marquee_freshness", label: "Marquee Freshness", icon: CalendarCheck },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [articles, setArticles] = useState([]);
  const [ads, setAds] = useState({});
  const [subs, setSubs] = useState([]);
  const [sources, setSources] = useState([]);
  const [health, setHealth] = useState([]);
  const [scraping, setScraping] = useState(false);
  const [articleIsland, setArticleIsland] = useState("all");

  useEffect(() => {
    if (!localStorage.getItem("ir_token")) nav("/admin");
  }, [nav]);

  const logout = () => {
    localStorage.removeItem("ir_token");
    nav("/admin");
  };

  const load = useCallback(async () => {
    try {
      const articleParams = articleIsland !== "all" ? { island: articleIsland } : {};
      const [a, art, ad, sb, src, hl] = await Promise.all([
        api.get("/admin/analytics"),
        api.get("/admin/articles", { params: articleParams }),
        api.get("/admin/ads"),
        api.get("/admin/subscribers"),
        api.get("/admin/sources"),
        api.get("/admin/source-health"),
      ]);
      setAnalytics(a.data);
      setArticles(art.data);
      const adMap = {};
      ad.data.forEach((d) => {
        if (!adMap[d.slot]) adMap[d.slot] = [];
        adMap[d.slot].push(d);
      });
      setAds(adMap);
      setSubs(sb.data);
      setSources(src.data);
      setHealth(hl.data);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        toast.error("Failed to load");
      }
    }
  }, [articleIsland]);

  useEffect(() => {
    load();
  }, [load]);

  const runScrape = async () => {
    setScraping(true);
    try {
      await api.post("/admin/scrape");
      toast.success("Scrape started in background");
      const start = Date.now();
      const poll = async () => {
        try {
          const s = await api.get("/admin/scrape/status");
          if (!s.data.running || Date.now() - start > 90000) {
            setScraping(false);
            load();
            return;
          }
          setTimeout(poll, 3000);
        } catch {
          setScraping(false);
        }
      };
      setTimeout(poll, 3000);
    } catch {
      toast.error("Scrape failed");
      setScraping(false);
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/articles/${id}`);
    toast.success("Deleted");
    load();
  };

  const featureArticle = async (id, isFeatured) => {
    if (isFeatured) {
      await api.post(`/admin/articles/${id}/unfeature`);
    } else {
      await api.post(`/admin/articles/${id}/feature`);
    }
    toast.success(isFeatured ? "Unpinned" : "Pinned as featured");
    load();
  };

  const saveAd = async (slot, payload) => {
    await api.put(`/admin/ads/${slot}`, payload);
    toast.success("Saved");
    load();
  };

  const deleteAd = async (slot, variant) => {
    if (!window.confirm(`Remove this ${slot} creative for "${variant}"?`)) return;
    await api.delete(`/admin/ads/${slot}`, { params: { variant } });
    toast.success("Removed");
    load();
  };

  const deleteSub = async (id) => {
    await api.delete(`/admin/subscribers/${id}`);
    load();
  };

  const retranslate = async () => {
    await api.post("/admin/translate-missing");
    toast.success("Re-translating articles in background");
  };

  const refreshImages = async () => {
    await api.post("/admin/refresh-images");
    toast.success("Re-fetching source images in background. Refresh in 1-2 min.");
  };

  const toggleSource = async (name) => {
    await api.post(`/admin/sources/${encodeURIComponent(name)}/toggle`);
    load();
  };

  const sourcesByIsland = sources.reduce((acc, s) => {
    (acc[s.island] = acc[s.island] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-white text-[#1A1A1A]">
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <Link to="/" className="font-serif-display text-xl">
            <span className="text-[#1A1A1A]">Islands</span>
            <span className="text-[#0AADA0]">Daily</span>
            <span className="ml-3 text-[11px] uppercase tracking-wider text-[#6B7280]">
              Admin
            </span>
          </Link>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={refreshImages}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs text-[#6B7280] hover:text-[#0AADA0] hover:bg-[#0AADA0]/10 transition-colors"
              data-testid="admin-refresh-images"
            >
              <ImageIcon size={14} /> Refresh images
            </button>
            <button
              onClick={retranslate}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs text-[#6B7280] hover:text-[#0AADA0] hover:bg-[#0AADA0]/10 transition-colors"
              data-testid="admin-translate-missing"
            >
              <Languages size={14} /> Retranslate
            </button>
            <button
              onClick={runScrape}
              disabled={scraping}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E63946] text-white text-xs hover:bg-[#cf2f3c] disabled:opacity-60 transition-colors"
              data-testid="admin-run-scrape"
            >
              <RefreshCw size={14} className={scraping ? "animate-spin" : ""} />
              {scraping ? "Scraping..." : "Run scraper"}
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs text-[#6B7280] hover:text-[#1A1A1A] transition-colors"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
        <nav
          className="max-w-7xl mx-auto px-6 flex flex-wrap gap-x-1 gap-y-1"
          data-testid="admin-tab-strip"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`px-4 py-3 text-xs uppercase tracking-wider flex items-center gap-2 border-b-2 transition-colors ${
                  active
                    ? "border-[#0AADA0] text-[#0AADA0]"
                    : "border-transparent text-[#6B7280] hover:text-[#1A1A1A]"
                }`}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <ConfigHealthBanner />
        <ScraperHealthBanner />
        {tab === "overview" && <OverviewTab analytics={analytics} />}

        {tab === "articles" && (
          <ArticlesTab
            articles={articles}
            articleIsland={articleIsland}
            setArticleIsland={setArticleIsland}
            onFeature={featureArticle}
            onDelete={deleteArticle}
          />
        )}

        {tab === "health" && (
          <div className="space-y-4">
            <ScraperHealthCards />
            <HealthTab health={health} reload={load} />
          </div>
        )}

        {tab === "sources" && (
          <SourcesTab
            sourcesByIsland={sourcesByIsland}
            toggleSource={toggleSource}
            reload={load}
          />
        )}

        {tab === "ads" && (
          <AdsTab ads={ads} onSave={saveAd} onDelete={deleteAd} />
        )}

        {tab === "subscribers" && (
          <SubscribersTab subscribers={subs} onDelete={deleteSub} onRefresh={load} />
        )}

        {tab === "compliance" && <ComplianceTab />}

        {tab === "digest" && <DigestTab subscribers={subs} />}

        {tab === "sponsors" && <SponsorsTab />}

        {tab === "islands" && <IslandsTab />}

        {tab === "digest_analytics" && <DigestAnalyticsTab />}

        {tab === "essentials" && <EssentialsTab />}

        {tab === "self_serve_ads" && <SelfServeAdsTab />}
        {tab === "sponsor_slots" && <SponsorSlotsTab />}

        {tab === "dedup" && <DedupReviewTab />}

        {tab === "history" && <HistoryReviewTab />}

        {tab === "marquee_freshness" && <MarqueeFreshnessTab />}
      </main>
    </div>
  );
}
